import React from "react"
import { makeStyles } from "@material-ui/core/styles"
import { Typography } from "@material-ui/core/index"
import MainToolbar from "../components/main-toolbar"
import Mission from "../components/mission"
import ClinicImageSection from "../components/ClinicImgageSection"
import SignatureExamples from "../components/SignatureExamples"
import Layout from "../components/layout"
import SEO from "../components/seo"
import FollowUpBar, { FOLLOW_UP_ROOM } from "../components/FollowUpBar"
import farisCutout from "../images/doctors/callum-faris-cutout.webp"
import susanCutout from "../images/doctors/susan-schmidt-cutout.webp"
import duoWide1280Webp from "../images/home/duo-wide-1280.webp"
import duoWide2560Webp from "../images/home/duo-wide-2560.webp"
import duoWide1280Jpg from "../images/home/duo-wide-1280.jpg"
import duoWide2560Jpg from "../images/home/duo-wide-2560.jpg"
import duoMobileWebp from "../images/home/duo-mobile.webp"
import duoMobileJpg from "../images/home/duo-mobile.jpg"
import DoctorsShowcase from "../components/DoctorsShowcase"
import harvardLogo from "../images/logos/harvard-medical-school.jpg"
import bartsLogo from "../images/logos/barts-london.png"
import ubcLogo from "../images/logos/university-of-british-columbia.png"
import caiusLogo from "../images/logos/gonville-caius-cambridge.png"
import helsinkiLogo from "../images/logos/university-of-helsinki.png"
import nvcgLogo from "../images/logos/nvcg.png"
import { injectIntl, Link } from "gatsby-plugin-intl"
import { ACCENT, CTA_BUTTON_PRIMARY } from "../styles/cta"

const BOOKING_URL =
  "https://schedule.clinicminds.com/?clinic=a8f5f0f3-1e41-11e9-881b-0a599d16f52a"

const CONTENT_COL = 1280
// Where the hero text column ends: the content column is centred, has 32px
// left padding, and the text block is capped at 620px.
const TEXT_END = `(max(0px, (100% - ${CONTENT_COL}px) / 2) + 652px)`
const HERO_FIELD = "#141110"
const crispShadow = "0 1px 3px rgba(0,0,0,0.35)"
// Where the hero text starts on a phone: one screen, less the nav bar and what
// must show on that first screen (the headline and the lines under it).
const PHONE_TEXT_TOP = "min(100svh - 60px - 290px, 100vw)"
const PHOTO_BELOW_HERO = 110

// The photo starts where the hero text ends, fades in over PHOTO_FADE and
// fades out below the hero, behind the follow-up bar.
const PHOTO_FADE = "clamp(140px, 11vw, 220px)"
const PHOTO_FADE_LEFT = `linear-gradient(90deg, transparent 0, rgba(0,0,0,0.35) calc(${PHOTO_FADE} * 0.4), rgba(0,0,0,0.8) calc(${PHOTO_FADE} * 0.72), #000 ${PHOTO_FADE})`
const PHOTO_FADE_BOTTOM =
  "linear-gradient(180deg, #000 0, #000 calc(100% - 260px), rgba(0,0,0,0.6) calc(100% - 170px), rgba(0,0,0,0.2) calc(100% - 80px), transparent 100%)"
// The photo (a 1180x941 crop) is sized and placed from the hero's height,
// written as a*h + b in cqh on a container exactly as tall as the hero. Two
// points in the photo place it: the top of Dr. Faris's hair sits level with
// the headline's first line (HEADLINE is a fit of where that lands) but never
// closer than 120px to the top, and the left edge of Dr. Schmidt's hair sits
// just inside the fade.
const lin = ([a, b]) => `${(a * 100).toFixed(2)}cqh + ${b.toFixed(1)}px`
const scale = ([a, b], k) => [a * k, b * k]
const PHOTO_H = scale([1, 110], 1.1 * (941 / 850))
const PHOTO_W = scale(PHOTO_H, 1180 / 941)
const HEADLINE = [0.396, -109]
const FARIS_HAIR = 247 / 941
const SCHMIDT_HAIR = 358 / 1180
const PHOTO_TOP = `max(${lin([
  HEADLINE[0] - FARIS_HAIR * PHOTO_H[0],
  HEADLINE[1] - FARIS_HAIR * PHOTO_H[1],
])}, 120px - (${lin(scale(PHOTO_H, FARIS_HAIR))}))`
const PHOTO_LEFT = `max(${PHOTO_FADE} - 20px - (${lin(
  scale(PHOTO_W, SCHMIDT_HAIR)
)}), 100cqw - (${lin(PHOTO_W)}))`

const useStyles = makeStyles((theme) => ({
  "@global": {
    body: {
      backgroundColor: "#000",
    },
    ul: {
      margin: 0,
      padding: 0,
    },
    li: {
      listStyle: "none",
    },
  },
  mainContainer: {
    backgroundColor: "#202020",
    overflow: "hidden",
  },
  heroWrap: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    backgroundColor: HERO_FIELD,
    minHeight: "clamp(560px, 74vh, 900px)",
    // Lifted above the next section, so the photo shows where it runs on
    // below the hero.
    [theme.breakpoints.up("md")]: {
      zIndex: 3,
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 0,
    },
  },
  heroMedia: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    [theme.breakpoints.up("md")]: {
      left: `calc(${TEXT_END} - 30px)`,
      bottom: -PHOTO_BELOW_HERO,
      WebkitMaskImage: `${PHOTO_FADE_LEFT}, ${PHOTO_FADE_BOTTOM}`,
      maskImage: `${PHOTO_FADE_LEFT}, ${PHOTO_FADE_BOTTOM}`,
      WebkitMaskComposite: "source-in",
      maskComposite: "intersect",
    },
  },
  heroPicture: {
    display: "block",
    [theme.breakpoints.up("md")]: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: `calc(100% - ${PHOTO_BELOW_HERO}px)`,
      containerType: "size",
    },
  },
  heroImg: {
    position: "absolute",
    objectFit: "cover",
    [theme.breakpoints.up("md")]: {
      top: PHOTO_TOP,
      left: PHOTO_LEFT,
      width: "auto",
      maxWidth: "none",
      height: `calc(${lin(PHOTO_H)})`,
    },
    // On phones the photo starts under the 58px menu bar: the photo has too
    // little room above Dr. Faris's head to sit behind it.
    [theme.breakpoints.down("sm")]: {
      top: 58,
      left: 0,
      width: "100%",
      height: `calc(${PHONE_TEXT_TOP})`,
      objectPosition: "50% 0",
    },
  },
  heroInner: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  heroContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
    maxWidth: CONTENT_COL,
    margin: "0 auto",
    padding: "min(40px, 4vh) 32px 0",
    paddingBottom: `calc(${FOLLOW_UP_ROOM}px + min(32px, 3vh))`,
    [theme.breakpoints.down("sm")]: {
      flex: "0 0 auto",
      padding: theme.spacing(0, 2.5, 3.5),
      paddingTop: `calc(${PHONE_TEXT_TOP})`,
      background: `linear-gradient(180deg, transparent 0, transparent calc(${PHONE_TEXT_TOP} - 70px), ${HERO_FIELD} calc(${PHONE_TEXT_TOP}), ${HERO_FIELD} 100%)`,
    },
  },
  heroText: {
    maxWidth: 620,
  },
  mottoBlock: {
    paddingLeft: "1rem",
    borderLeft: "3px solid #f3e3cc",
  },
  motto: {
    color: "#fff",
    textTransform: "uppercase",
    fontWeight: 400,
    lineHeight: 1.12,
    textShadow: crispShadow,
    fontSize: "clamp(1.9rem, min(3.6vw, 5.2vh), 3rem)",
    // "AANGEZICHTSCHIRURGIE" cannot break and measures up to 12.4px wide per
    // 1px of font size (weight 400); this keeps it inside a phone's text column.
    [theme.breakpoints.down("xs")]: {
      fontSize: "clamp(1.2rem, calc(7.9vw - 6px), 1.9rem)",
    },
  },
  city: {
    color: "#f3e3cc",
    textTransform: "uppercase",
    letterSpacing: "0.32em",
    fontSize: "0.78rem",
    fontWeight: 500,
    marginTop: theme.spacing(1.5),
  },
  cityAfterNames: { marginTop: theme.spacing(0.5) },
  nowrap: { whiteSpace: "nowrap" },
  signature: {
    marginTop: "min(48px, 4.5vh)",
    [theme.breakpoints.down("sm")]: { marginTop: theme.spacing(4) },
  },
  eyebrow: {
    color: ACCENT,
    textTransform: "uppercase",
    letterSpacing: "0.18em",
    fontSize: "0.8rem",
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  signatureTitle: {
    color: "#fff",
    fontWeight: 400,
    lineHeight: 1.2,
    textShadow: crispShadow,
    fontSize: "clamp(1.5rem, 2.4vw, 2.1rem)",
  },
  textLinkCta: {
    "&&": {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      width: "auto",
      minHeight: 0,
      padding: "0 0 3px",
      background: "none",
      border: "none",
      borderBottom: `1px solid ${ACCENT}`,
      borderRadius: 0,
      boxShadow: "none",
      color: ACCENT,
      fontSize: "1.05rem",
      fontWeight: 500,
      textDecoration: "none",
      "&:hover": { opacity: 0.8, background: "none" },
    },
  },
  heroActions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "16px 28px",
    marginTop: theme.spacing(2.5),
  },
  heroCta: {
    ...CTA_BUTTON_PRIMARY,
    width: "auto",
    minHeight: 50,
    padding: "0 30px",
  },
}))

const Frontpage = ({ intl, location }) => {
  const classes = useStyles()
  const t = (id) => intl.formatMessage({ id })
  const specialtiesOf = (key) =>
    [1, 2, 3, 4, 5]
      .map((n) => `home.doctors.${key}.specialty${n}`)
      .filter((id) => intl.messages[id])
      .map(t)

  const doctors = [
    {
      id: "callum",
      key: "faris",
      cutout: farisCutout,
      cutoutSize: [1114, 1101],
      instagram: "doctor.callum.faris",
      logos: [
        { src: harvardLogo, alt: "Harvard Medical School", onBlack: true },
        {
          src: caiusLogo,
          alt: "Gonville & Caius College, University of Cambridge",
        },
        {
          src: bartsLogo,
          alt: "Barts and The London School of Medicine and Dentistry",
        },
        { src: ubcLogo, alt: "The University of British Columbia" },
      ],
    },
    {
      id: "susan",
      key: "schmidt",
      cutout: susanCutout,
      cutoutSize: [936, 1020],
      instagram: "drsusanschmidt",
      logos: [
        { src: helsinkiLogo, alt: "University of Helsinki" },
        { src: nvcgLogo, alt: "NVCG — Dutch Society of Cosmetic Medicine" },
      ],
    },
  ]

  return (
    <Layout>
      <SEO
        lang={intl.formatMessage({ id: "global.lang" })}
        title={intl.formatMessage({ id: "home.SEOtitle" })}
        description={intl.formatMessage({ id: "home.SEO" })}
        path={location.pathname}
      />
      <main className={classes.mainContainer}>
        <div className={classes.heroWrap}>
          <div className={classes.heroMedia}>
            <picture className={classes.heroPicture}>
              <source
                media="(max-width: 959.95px)"
                type="image/webp"
                srcSet={duoMobileWebp}
              />
              <source media="(max-width: 959.95px)" srcSet={duoMobileJpg} />
              <source
                type="image/webp"
                srcSet={`${duoWide1280Webp} 1280w, ${duoWide2560Webp} 2560w`}
                sizes="55vw"
              />
              <img
                src={duoWide2560Jpg}
                srcSet={`${duoWide1280Jpg} 1280w, ${duoWide2560Jpg} 2560w`}
                sizes="55vw"
                alt={t("home.hero.photoAlt")}
                className={classes.heroImg}
                loading="eager"
                fetchpriority="high"
              />
            </picture>
          </div>
          <div className={classes.heroInner}>
            <MainToolbar />
            <div className={classes.heroContent}>
              <div className={classes.heroText}>
                <div className={classes.mottoBlock}>
                  <Typography component="h1" className={classes.motto}>
                    {t("home.hero.motto")}
                  </Typography>
                  <Typography className={classes.city}>
                    <span className={classes.nowrap}>
                      {t("home.hero.doctorsLine1")}
                    </span>{" "}
                    <span className={classes.nowrap}>
                      {t("home.hero.doctorsLine2")}
                    </span>
                  </Typography>
                  <Typography
                    className={`${classes.city} ${classes.cityAfterNames}`}
                  >
                    {t("home.hero.city")}
                  </Typography>
                </div>
                <FollowUpBar />
                <div className={classes.signature}>
                  <Typography className={classes.eyebrow}>
                    {t("home.hero.signatureLabel")}
                  </Typography>
                  <Typography component="h2" className={classes.signatureTitle}>
                    {t("home.hero.procedure")}
                  </Typography>
                  <div className={classes.heroActions}>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.heroCta}
                    >
                      {t("home.hero.makeAppointment")}
                    </a>
                    <Link
                      to="/deep-plane-facelift/"
                      className={classes.textLinkCta}
                    >
                      {t("home.hero.aboutProcedure")} →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <DoctorsShowcase
          afterBar
          doctors={doctors}
          t={t}
          specialtiesOf={specialtiesOf}
          bookingUrl={BOOKING_URL}
        />

        <SignatureExamples />
        <Mission />
        <ClinicImageSection />
      </main>
    </Layout>
  )
}

export default injectIntl(Frontpage)

//Material UI break points:
// xs (extra-small): 0px or larger
// sm (small): 600px or larger
// md (medium): 960px or larger
// lg (large): 1280px or larger
// xl (extra-large): 1920px or larger

// Classic media query
// ['@media (max-width:780px)']: {
//   width: '90%',
// },
